import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Undo, Trash2, Check, Type, MoveRight, X } from 'lucide-react';

export default function ChartAnnotator({ imageSrc, onSave, onClose }) {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('indication'); // indication, correction, continuation, text, freehand
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [textPos, setTextPos] = useState(null);
  const [imageObj, setImageObj] = useState(null);

  const TOOL_COLORS = {
    indication: '#06b6d4', // Cyan
    correction: '#eab308', // Yellow
    continuation: '#22c55e', // Green
    freehand: '#ec4899', // Pink
    text: '#3b82f6', // Blue
  };

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;
    img.onload = () => {
      setImageObj(img);
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.width > 1200 ? 1200 : img.width;
      canvas.height = (img.height / img.width) * canvas.width;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      saveState();
    };
  }, [imageSrc]);

  const saveState = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      setHistory((prev) => [...prev.slice(-10), canvas.toDataURL()]);
    }
  };

  const undo = () => {
    if (history.length <= 1) return;
    const newHist = [...history];
    newHist.pop();
    const lastState = newHist[newHist.length - 1];
    setHistory(newHist);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = lastState;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
  };

  const clearCanvas = () => {
    if (!imageObj) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageObj, 0, 0, canvas.width, canvas.height);
    setHistory([]);
    saveState();
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e) => {
    const coords = getCanvasCoords(e);
    setStartPos(coords);

    if (tool === 'text') {
      setTextPos(coords);
      return;
    }

    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    ctx.strokeStyle = TOOL_COLORS[tool] || '#06b6d4';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const coords = getCanvasCoords(e);
    const ctx = canvasRef.current.getContext('2d');

    if (tool === 'freehand') {
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    }
  };

  const handleMouseUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const coords = getCanvasCoords(e);
    const ctx = canvasRef.current.getContext('2d');

    if (['indication', 'correction', 'continuation'].includes(tool)) {
      // Draw styled line + label badge
      ctx.beginPath();
      ctx.moveTo(startPos.x, startPos.y);
      ctx.lineTo(coords.x, coords.y);
      ctx.strokeStyle = TOOL_COLORS[tool];
      ctx.lineWidth = 4;
      ctx.stroke();

      // Arrow head
      const angle = Math.atan2(coords.y - startPos.y, coords.x - startPos.x);
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      ctx.lineTo(coords.x - 12 * Math.cos(angle - Math.PI / 6), coords.y - 12 * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(coords.x - 12 * Math.cos(angle + Math.PI / 6), coords.y - 12 * Math.sin(angle + Math.PI / 6));
      ctx.fillStyle = TOOL_COLORS[tool];
      ctx.fill();

      // Badge label
      const label = tool.toUpperCase();
      ctx.font = 'bold 13px sans-serif';
      const textWidth = ctx.measureText(label).width;
      const midX = (startPos.x + coords.x) / 2;
      const midY = (startPos.y + coords.y) / 2 - 10;

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(midX - textWidth / 2 - 6, midY - 14, textWidth + 12, 20);
      ctx.strokeStyle = TOOL_COLORS[tool];
      ctx.lineWidth = 1.5;
      ctx.strokeRect(midX - textWidth / 2 - 6, midY - 14, textWidth + 12, 20);

      ctx.fillStyle = TOOL_COLORS[tool];
      ctx.fillText(label, midX - textWidth / 2, midY);
    }

    saveState();
  };

  const handleAddText = () => {
    if (!textInput || !textPos) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.font = 'bold 16px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#000000';
    ctx.shadowBlur = 4;
    ctx.fillText(textInput, textPos.x, textPos.y);
    ctx.shadowBlur = 0;
    setTextInput('');
    setTextPos(null);
    saveState();
  };

  const handleFinish = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      const annotatedFile = new File([blob], 'annotated_chart.png', { type: 'image/png' });
      onSave(annotatedFile, canvas.toDataURL());
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header toolbar */}
        <div className="p-3 bg-slate-800/90 border-b border-slate-700 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-semibold uppercase text-cyan-400 tracking-wider mr-2">
              Annotate Setup:
            </span>
            <button
              onClick={() => setTool('indication')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                tool === 'indication'
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <MoveRight className="w-3.5 h-3.5 text-cyan-400" />
              1. Indication
            </button>
            <button
              onClick={() => setTool('correction')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                tool === 'correction'
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <MoveRight className="w-3.5 h-3.5 text-yellow-400" />
              2. Correction
            </button>
            <button
              onClick={() => setTool('continuation')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                tool === 'continuation'
                  ? 'bg-green-500/20 text-green-400 border border-green-500'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <MoveRight className="w-3.5 h-3.5 text-green-400" />
              3. Continuation
            </button>
            <button
              onClick={() => setTool('freehand')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                tool === 'freehand'
                  ? 'bg-pink-500/20 text-pink-400 border border-pink-500'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <Pencil className="w-3.5 h-3.5 text-pink-400" />
              Freehand
            </button>
            <button
              onClick={() => setTool('text')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                tool === 'text'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <Type className="w-3.5 h-3.5 text-blue-400" />
              Text
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={undo}
              className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white border border-slate-700"
              title="Undo"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={clearCanvas}
              className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-red-400 border border-slate-700"
              title="Clear All"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white border border-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleFinish}
              className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-950 font-bold text-xs flex items-center gap-1.5 hover:opacity-90 transition-all shadow-md"
            >
              <Check className="w-4 h-4" />
              Save Annotation
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="relative flex-1 overflow-auto bg-slate-950 flex items-center justify-center p-4 min-h-[400px]">
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            className="cursor-crosshair border border-slate-800 rounded-lg shadow-lg max-w-full"
          />

          {textPos && (
            <div
              className="absolute z-10 bg-slate-800 p-2 rounded-lg border border-cyan-500/50 shadow-xl flex gap-2"
              style={{ left: `${textPos.x}px`, top: `${textPos.y}px` }}
            >
              <input
                type="text"
                autoFocus
                placeholder="Type chart note..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddText()}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white outline-none focus:border-cyan-400"
              />
              <button
                onClick={handleAddText}
                className="px-2 py-1 bg-cyan-500 text-slate-950 text-xs font-bold rounded"
              >
                Add
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
